/**
 * Platen pixels (Phase 86, Round 4): what Large and Small are on each platen.
 *
 * The UI calls this "Platen variants" (Settings, Production, Printing files;
 * zh 压板规格); the code calls it "platen pixels", the data it stores. One doc
 * per org (`org_platen_pixels`, backend only) holds, per platen, the Large
 * file's width and height in px and the org's DEFAULT for Small: null =
 * uploaded by hand, or a scale in basis points (1000 to 10000 = 10% to 100%)
 * = made from Large at that percent (R4-1, R4-2, R4-12).
 *
 * This is the ONE math and validation module: the Settings preview, the pixel
 * check, By colour and By set all read these functions, so what Settings
 * shows is what the server makes.
 *
 * - Platen codes are the closed `DTG_PLATEN_CODES` list; an org can never add
 *   one (R4-7, D-06). Anything else in a stored doc is dropped.
 * - With no doc, every number is `PLATEN_DIMS` (read, never copied) and there
 *   is no Small preset (R4-1): byte-identical to before this module.
 * - A Small file keeps the Large file's pixels; only the art inside it is
 *   scaled, top centre (R4-3). A size with no platen variant picked is Large
 *   (R4-5).
 *
 * PURE (no I/O) and never throws on stored data.
 */
import { DTG_PLATEN_CODES, PLATEN_DIMS, getOwn } from './rip-routing.js';
/** Large px per side, and the Small scale in basis points. */
export const PLATEN_PIXEL_BOUNDS = {
    minPx: 600,
    maxPx: 9000,
    minScaleBp: 1000,
    maxScaleBp: 10000,
};
/** 300 DPI: px per inch of a print file. */
const PX_PER_INCH = 300;
// ---------------------------------------------------------------------------
// Validation.
// ---------------------------------------------------------------------------
/** A whole number of px inside the Large bounds. */
export function isValidLargePx(value) {
    return (typeof value === 'number' &&
        Number.isInteger(value) &&
        value >= PLATEN_PIXEL_BOUNDS.minPx &&
        value <= PLATEN_PIXEL_BOUNDS.maxPx);
}
/** A whole number of basis points inside the Small scale bounds. */
export function isValidScaleBp(value) {
    return (typeof value === 'number' &&
        Number.isInteger(value) &&
        value >= PLATEN_PIXEL_BOUNDS.minScaleBp &&
        value <= PLATEN_PIXEL_BOUNDS.maxScaleBp);
}
/** The PLATEN_DIMS default Large of a platen (a fresh object each call). */
export function defaultLargePixels(platen) {
    const dims = PLATEN_DIMS[platen];
    return { width: dims.w, height: dims.h };
}
function sameSize(a, b) {
    return a.width === b.width && a.height === b.height;
}
/**
 * Keep only what is valid: the closed platen keys (R4-7), a Large with both
 * sides whole numbers in bounds, a Small scale in bounds. A bad field falls
 * back to its default; an entry left empty is dropped. Never throws.
 */
export function sanitizePlatenPixels(raw) {
    const out = {};
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return out;
    for (const platen of DTG_PLATEN_CODES) {
        const entry = getOwn(raw, platen);
        if (!entry || typeof entry !== 'object' || Array.isArray(entry))
            continue;
        const clean = {};
        const large = getOwn(entry, 'large');
        if (large && typeof large === 'object' && !Array.isArray(large)) {
            const width = getOwn(large, 'width');
            const height = getOwn(large, 'height');
            if (isValidLargePx(width) && isValidLargePx(height))
                clean.large = { width, height };
        }
        const bp = getOwn(entry, 'smallScaleBp');
        if (isValidScaleBp(bp))
            clean.smallScaleBp = bp;
        if (clean.large !== undefined || clean.smallScaleBp !== undefined)
            out[platen] = clean;
    }
    return out;
}
/** Every platen with its defaults filled in (R4-1). `null` = no doc. */
export function effectivePlatenPixels(doc) {
    const out = {};
    for (const platen of DTG_PLATEN_CODES) {
        const fallback = defaultLargePixels(platen);
        const entry = doc ? getOwn(doc, platen) : undefined;
        const large = entry?.large ? { width: entry.large.width, height: entry.large.height } : fallback;
        const smallScaleBp = entry?.smallScaleBp ?? null;
        out[platen] = { large, smallScaleBp, isDefault: smallScaleBp === null && sameSize(large, fallback) };
    }
    return out;
}
/**
 * The doc to store: entries equal to the default with no Small preset are
 * dropped (no row and the defaults are the same thing), and a Large equal to
 * the default is left out of its entry. All defaults = `{}`.
 */
export function minimizePlatenPixels(doc) {
    const effective = effectivePlatenPixels(doc);
    const out = {};
    for (const platen of DTG_PLATEN_CODES) {
        const e = effective[platen];
        if (e.isDefault)
            continue;
        const entry = {};
        if (!sameSize(e.large, defaultLargePixels(platen)))
            entry.large = e.large;
        if (e.smallScaleBp !== null)
            entry.smallScaleBp = e.smallScaleBp;
        out[platen] = entry;
    }
    return out;
}
// ---------------------------------------------------------------------------
// Small: percent, px and placement.
// ---------------------------------------------------------------------------
/** The Small art px inside a Large-sized file: round(side x bp / 10000). */
export function smallArtPixels(large, bp) {
    return {
        width: Math.round((large.width * bp) / 10000),
        height: Math.round((large.height * bp) / 10000),
    };
}
/** Basis points from a typed Small art side, clamped to the scale bounds. */
export function scaleBpFromSmallPx(px, largeSide) {
    if (!Number.isFinite(px) || !Number.isFinite(largeSide) || largeSide <= 0)
        return PLATEN_PIXEL_BOUNDS.minScaleBp;
    const bp = Math.round((px * 10000) / largeSide);
    return Math.min(PLATEN_PIXEL_BOUNDS.maxScaleBp, Math.max(PLATEN_PIXEL_BOUNDS.minScaleBp, bp));
}
/**
 * Basis points from percent text (after the caller's `normalizeNumericText`):
 * up to 3 whole digits and up to 2 decimals. Empty text is `null`; text that
 * is not such a number is `'invalid'`. The result is NOT range-checked: use
 * {@link isValidScaleBp}. String arithmetic, so no float drift.
 */
export function scaleBpFromPercentText(text) {
    const t = text.trim();
    if (t === '')
        return null;
    const m = /^(\d{1,3})(?:\.(\d{1,2}))?$/.exec(t);
    if (!m)
        return 'invalid';
    const whole = Number(m[1]);
    const frac = Number((m[2] ?? '').padEnd(2, '0'));
    return whole * 100 + frac;
}
/** A basis-point scale as percent text: 9000 "90", 8750 "87.5", 8725 "87.25". */
export function formatScalePercent(bp) {
    const whole = Math.floor(bp / 100);
    const frac = bp % 100;
    if (frac === 0)
        return String(whole);
    return `${whole}.${String(frac).padStart(2, '0').replace(/0$/, '')}`;
}
/**
 * The ONE placement of the Small art: scaled by `bp`, top centre (top 0,
 * left floor((canvas - art) / 2)). The derive function, By set and the
 * Settings preview all use it (R4-3).
 */
export function smallPlacement(canvas, bp) {
    const art = smallArtPixels(canvas, bp);
    return { ...art, left: Math.floor((canvas.width - art.width) / 2), top: 0 };
}
// ---------------------------------------------------------------------------
// Lookups.
// ---------------------------------------------------------------------------
/**
 * The px a platen variant file is checked against: the platen's Large for
 * every variant (a Small file keeps the Large pixels, with the art scaled
 * inside it), so the variant is not an input (R4-5).
 */
export function platenFilePixels(pixels, platen) {
    const large = pixels[platen].large;
    return { width: large.width, height: large.height };
}
/** The platen's nominal size in inches at 300 DPI, from its code ("14x16" = 14 x 16). */
export function platenNominalInches(platen) {
    const [w, h] = platen.split('x').map(Number);
    return { width: w, height: h };
}
/** True when a Large is bigger than the platen's nominal inches at 300 DPI (warn, never block). */
export function isLargeAboveNominal(platen, large) {
    const inches = platenNominalInches(platen);
    return large.width > inches.width * PX_PER_INCH || large.height > inches.height * PX_PER_INCH;
}
/**
 * True only while a side's Small file is the one made from Large: the made
 * marker is set, equals the side's source fingerprint, and the side is Small
 * (R4-4). A hand upload replaces the fingerprint, so the marker stops
 * matching and the file counts as uploaded by hand from then on.
 */
export function isMadeFromLarge(side) {
    return (side.platenVariant === 'Small' &&
        typeof side.madeFingerprint === 'string' &&
        side.madeFingerprint.length > 0 &&
        side.madeFingerprint === side.sourceFingerprint);
}
